import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Self,
  SkipSelf,
} from '@angular/core';
import {FormGroup, FormGroupDirective, FormGroupName} from '@angular/forms';
import {merge, NEVER, Observable, Subject, Subscription} from 'rxjs';
import {FORM_VALIDATOR_CONFIGURATION, FormEvent, FormEventType, FormValidatorConfig} from "@core/libs";

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[formGroup], [formGroupName]',
  exportAs: 'formGroupValidator',
  standalone: false
})
export class FormGroupValidatorDirective implements AfterViewInit, OnDestroy {
  @Input() skipValidate = false;

  get parent(): Partial<FormGroupValidatorDirective> {
    return this.parentFormGroupValidatorDirective;
  }

  get skipValid(): boolean {
    return (
      this.config.skipValidate ??
      this.skipValidate ??
      Boolean(this.parent?.skipValid)
    );
  }

  get formGroup(): FormGroup {
    return this.formGroupDirective
      ? this.formGroupDirective.form
      : this.formGroupName.control;
  }

  get events$(): Observable<FormEvent> {
    return merge(this._events$ ?? NEVER, this.parent?.events$ ?? NEVER);
  }

  private _events$ = new Subject<FormEvent>();
  private _subscriptions = new Subscription();

  constructor(
    private readonly elementRef: ElementRef,
    @Self() @Optional() private readonly formGroupName: FormGroupName,
    @Self() @Optional() private readonly formGroupDirective: FormGroupDirective,
    @Inject(FORM_VALIDATOR_CONFIGURATION) private readonly config: FormValidatorConfig,
    @SkipSelf() @Optional() private readonly parentFormGroupValidatorDirective: FormGroupValidatorDirective
  ) {
  }

  ngAfterViewInit(): void {
    this.listenFormEvents();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  @HostListener('submit', ['$event']) onSubmit(event: Event): void {
    if (!(this.elementRef.nativeElement instanceof HTMLFormElement)) {
      return;
    }

    if (this.formGroup.invalid) {
      event.preventDefault();
    }

    if (this.formGroup.status != 'PENDING' && this.elementRef) {
      this.elementRef.nativeElement.classList.remove('ng-skip-pending')
    }

    this._events$.next({
      type: FormEventType.FormSubmit,
      value: this.formGroup,
    });
  }

  private listenFormEvents(): void {
    this._events$.next({
      type: FormEventType.FormInitial,
      value: this.formGroup,
    });

    this._subscriptions.add(
      this.formGroup.statusChanges.subscribe({
        next: status => {
          if (status === 'PENDING') {
            this.elementRef.nativeElement.classList.add('ng-skip-pending')
          }
          this._events$.next({
            type: FormEventType.StatusChange,
            value: this.formGroup,
          });
        }
      })
    );

    this._subscriptions.add(
      this.formGroup.valueChanges.subscribe({
        next: () => {
          this._events$.next({
            type: FormEventType.ValueChange,
            value: this.formGroup,
          });
        }
      })
    );
  }
}
